import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Clock, AlertTriangle, RefreshCw, Download, Tag } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { Badge } from '../ui/badge';
import { adminGet } from './adminApi';
import { toast } from 'sonner';
import { tagStyle, tagKey, primaryTag, collectTags } from '../../lib/jobTags';

const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(12, 0, 0, 0); return x; };
const iso = (d) => { const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000); return z.toISOString().slice(0, 10); };
const fmtH = (m) => { const v = Math.abs(Math.round(m || 0)); return `${Math.floor(v / 60)}h${String(v % 60).padStart(2, '0')}`; };
const dayLabel = (d) => new Date(`${d}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'short' }).replace('.', '');

/** Agency view: planned vs clocked hours of every Easer for one week, negative gaps in red. */
export function AdminTeamHours({ adminKey }) {
  const [anchor, setAnchor] = useState(() => startOfWeek(new Date()));
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tagFilter, setTagFilter] = useState(null); // job tag key (clean/green/diy) or null = everyone

  const range = useMemo(() => ({ start: anchor, end: addDays(anchor, 6) }), [anchor]);

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await adminGet('/api/admin/easer/hours', adminKey, { start: iso(range.start), end: iso(range.end) });
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Impossible de charger les heures');
      setData({ rows: [], totals: {}, days: [] });
    }
  }, [adminKey, range]);

  useEffect(() => { setData(null); load(); }, [load]);

  const exportCsv = () => {
    if (!rows.length) return toast.info('Rien à exporter');
    const head = ['Easer', 'Métier', 'Prévu (min)', 'Réalisé (min)', 'Écart (min)', 'Créneaux', 'Pointés', 'Terminés', 'Non pointés (passés)'];
    const lines = rows.map((r) => [r.name, (r.tags || []).join(' / '), r.planned_minutes, r.actual_minutes, r.diff_minutes, r.planned_count, r.clocked_count, r.done_count, r.not_clocked_past]);
    const csv = [head, ...lines].map((l) => l.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(';')).join('\n');
    const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `heures-easers-${iso(range.start)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const tags = useMemo(() => collectTags(data?.rows), [data]);
  const rows = useMemo(() => (data?.rows || []).filter((r) => !tagFilter || (r.tags || []).some((x) => tagKey(x) === tagFilter)), [data, tagFilter]);
  // Totals follow the job filter (sums of the visible rows); without filter the server totals are used.
  const t = useMemo(() => {
    if (!tagFilter) return data?.totals || {};
    const sum = (k) => rows.reduce((n, r) => n + (r[k] || 0), 0);
    return { easers: rows.length, planned_minutes: sum('planned_minutes'), actual_minutes: sum('actual_minutes'), diff_minutes: sum('actual_minutes') - sum('planned_minutes'),
      planned_count: sum('planned_count'), clocked_count: sum('clocked_count'), done_count: sum('done_count'), not_clocked_past: sum('not_clocked_past') };
  }, [data, rows, tagFilter]);
  const title = `Semaine du ${range.start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} au ${range.end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}`;

  return (
    <Card className="p-5" data-testid="admin-team-hours">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <h3 className="font-['Space_Grotesk'] font-semibold flex items-center gap-2"><Clock className="h-4 w-4" /> Heures de l'équipe</h3>
        <div className="ml-auto flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setAnchor((a) => addDays(a, -7))} aria-label="Semaine précédente" data-testid="team-hours-prev"><ChevronLeft className="h-5 w-5" /></Button>
          <span className="text-sm font-medium whitespace-nowrap" data-testid="team-hours-title">{title}</span>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setAnchor((a) => addDays(a, 7))} aria-label="Semaine suivante" data-testid="team-hours-next"><ChevronRight className="h-5 w-5" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={load} aria-label="Actualiser" data-testid="team-hours-refresh"><RefreshCw className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" className="h-8" onClick={exportCsv} data-testid="team-hours-export"><Download className="h-4 w-4 mr-1" /> CSV</Button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-3">Prévu = créneaux d'intervention de l'agenda Dolibarr · Réalisé = pointages Début/Fin (site ou application mobile). Écart négatif en rouge. Couleur de ligne = métier de l'Easer (tag Dolibarr).</p>
      {tags.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-4" role="group" aria-label="Filtrer par métier" data-testid="team-hours-tag-filter">
          <Tag className="h-3.5 w-3.5 text-muted-foreground" />
          <button type="button" onClick={() => setTagFilter(null)} aria-pressed={!tagFilter} data-testid="team-hours-tag-all"
            className={`h-8 rounded-full border px-3 text-xs font-medium transition-colors ${!tagFilter ? 'bg-foreground text-background border-foreground' : 'bg-card text-foreground border-border hover:bg-secondary'}`}>
            Tous ({data?.rows?.length || 0})
          </button>
          {tags.map((tg) => {
            const k = tagKey(tg); const n = (data?.rows || []).filter((r) => (r.tags || []).some((x) => tagKey(x) === k)).length;
            return (
              <button key={k} type="button" onClick={() => setTagFilter(tagFilter === k ? null : k)} aria-pressed={tagFilter === k} data-testid={`team-hours-tag-${k}`}
                className={`h-8 rounded-full border px-3 text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${tagStyle(tg).chip} ${tagFilter === k ? 'ring-2 ring-offset-1 ring-[hsl(var(--ring))]' : 'opacity-80 hover:opacity-100'}`}>
                <span className={`h-2 w-2 rounded-full ${tagStyle(tg).dot}`} /> {tg} ({n})
              </button>
            );
          })}
        </div>
      )}

      {data === null && <Skeleton className="h-40" />}
      {data && error && <p className="text-sm text-destructive" data-testid="team-hours-error">{error}</p>}
      {data && !error && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="rounded-xl bg-[hsl(var(--secondary))]/60 p-3"><p className="text-xs text-muted-foreground">Easers planifiées</p><p className="text-2xl font-bold" data-testid="team-hours-easers">{t.easers || 0}</p></div>
            <div className="rounded-xl bg-[hsl(var(--secondary))]/60 p-3"><p className="text-xs text-muted-foreground">Prévu</p><p className="text-2xl font-bold tabular-nums" data-testid="team-hours-planned">{fmtH(t.planned_minutes)}</p><p className="text-xs text-muted-foreground">{t.planned_count || 0} créneaux</p></div>
            <div className="rounded-xl bg-[hsl(var(--secondary))]/60 p-3"><p className="text-xs text-muted-foreground">Réalisé</p><p className="text-2xl font-bold tabular-nums text-[hsl(var(--primary))]" data-testid="team-hours-actual">{fmtH(t.actual_minutes)}</p><p className="text-xs text-muted-foreground">{t.clocked_count || 0} pointés · {t.done_count || 0} terminés</p></div>
            <div className="rounded-xl bg-[hsl(var(--secondary))]/60 p-3"><p className="text-xs text-muted-foreground">Écart</p><p className={`text-2xl font-bold tabular-nums ${(t.diff_minutes || 0) < 0 ? 'text-destructive' : 'text-[hsl(var(--brand-sage))]'}`} data-testid="team-hours-diff">{(t.diff_minutes || 0) < 0 ? '−' : '+'}{fmtH(t.diff_minutes)}</p>{t.not_clocked_past > 0 && <p className="text-xs text-destructive flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {t.not_clocked_past} créneau{t.not_clocked_past > 1 ? 'x' : ''} passé{t.not_clocked_past > 1 ? 's' : ''} non pointé{t.not_clocked_past > 1 ? 's' : ''}</p>}</div>
          </div>

          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="team-hours-empty">{tagFilter ? 'Aucun créneau planifié cette semaine pour ce métier.' : 'Aucun créneau planifié cette semaine.'}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="team-hours-table">
                <thead>
                  <tr className="text-left text-muted-foreground border-b border-border">
                    <th className="py-2 pr-3 font-medium">Easer</th>
                    {data.days.map((d) => <th key={d} className="py-2 px-1 font-medium text-center capitalize hidden lg:table-cell">{dayLabel(d)}</th>)}
                    <th className="py-2 px-2 font-medium text-right">Prévu</th>
                    <th className="py-2 px-2 font-medium text-right">Réalisé</th>
                    <th className="py-2 px-2 font-medium text-right">Écart</th>
                    <th className="py-2 pl-2 font-medium text-right">Créneaux</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const neg = r.diff_minutes < 0;
                    const main = primaryTag(r.tags); const st = tagStyle(main);
                    return (
                      <tr key={r.user_id} className={`border-b border-border/60 border-l-4 ${main ? st.accent : 'border-l-transparent'}`} data-testid="team-hours-row" data-tag={main ? tagKey(main) : ''}>
                        <td className="py-2 pl-2 pr-3 font-medium whitespace-nowrap">
                          <span className="inline-flex items-center gap-1.5 flex-wrap">
                            {r.name}
                            {(r.tags || []).map((tg) => <Badge key={tg} variant="outline" className={`text-[10px] py-0 px-1.5 font-medium ${tagStyle(tg).badge}`} data-testid="team-hours-row-tag">{tg}</Badge>)}
                            {r.in_progress > 0 && <Badge className="bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))] text-[10px]">en cours</Badge>}
                          </span>
                        </td>
                        {r.days.map((d) => (
                          <td key={d.day} className={`py-2 px-1 text-center tabular-nums text-xs hidden lg:table-cell ${d.planned ? `${main ? st.cell : 'bg-secondary/50'} rounded-sm` : ''} ${d.planned && !d.actual ? 'text-destructive font-medium' : d.planned ? '' : 'text-muted-foreground/50'}`} title={`${main ? `${main} · ` : ''}prévu ${fmtH(d.planned)} · réalisé ${fmtH(d.actual)}`}>
                            {d.planned ? `${fmtH(d.actual)}/${fmtH(d.planned)}` : '·'}
                          </td>
                        ))}
                        <td className="py-2 px-2 text-right tabular-nums">{fmtH(r.planned_minutes)}</td>
                        <td className="py-2 px-2 text-right tabular-nums font-medium">{fmtH(r.actual_minutes)}</td>
                        <td className={`py-2 px-2 text-right tabular-nums font-semibold ${neg ? 'text-destructive' : 'text-[hsl(var(--brand-sage))]'}`} data-testid="team-hours-row-diff">{neg ? '−' : '+'}{fmtH(r.diff_minutes)}</td>
                        <td className="py-2 pl-2 text-right text-xs text-muted-foreground whitespace-nowrap">{r.clocked_count}/{r.planned_count} pointés{r.not_clocked_past > 0 && <span className="text-destructive"> · {r.not_clocked_past} manquant{r.not_clocked_past > 1 ? 's' : ''}</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
